/**
 * Image Compression Utility
 * Compresses images to a maximum file size
 */

interface CompressionOptions {
    maxSizeMB: number;
    maxWidthOrHeight?: number;
    quality?: number;
}

/**
 * Compress an image file to the specified max size
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Compressed file
 */
export const compressImage = async (
    file: File,
    options: CompressionOptions = { maxSizeMB: 2, quality: 0.8 }
): Promise<File> => {
    const { maxSizeMB, maxWidthOrHeight = 1920, quality = 0.8 } = options;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    // If file is already smaller than max size, return it
    if (file.size <= maxSizeBytes) {
        return file;
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;

            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    reject(new Error('Failed to get canvas context'));
                    return;
                }

                // Calculate new dimensions
                let width = img.width;
                let height = img.height;

                if (maxWidthOrHeight) {
                    if (width > height) {
                        if (width > maxWidthOrHeight) {
                            height = (height * maxWidthOrHeight) / width;
                            width = maxWidthOrHeight;
                        }
                    } else {
                        if (height > maxWidthOrHeight) {
                            width = (width * maxWidthOrHeight) / height;
                            height = maxWidthOrHeight;
                        }
                    }
                }

                canvas.width = width;
                canvas.height = height;

                // Draw image on canvas
                ctx.drawImage(img, 0, 0, width, height);

                // Try different quality levels to meet size requirement
                let currentQuality = quality;
                let attempts = 0;
                const maxAttempts = 10;

                const tryCompress = () => {
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                reject(new Error('Failed to compress image'));
                                return;
                            }

                            // If blob is small enough or we've tried enough times, use it
                            if (blob.size <= maxSizeBytes || attempts >= maxAttempts) {
                                const compressedFile = new File([blob], file.name, {
                                    type: file.type,
                                    lastModified: Date.now(),
                                });
                                resolve(compressedFile);
                            } else {
                                // Reduce quality and try again
                                attempts++;
                                currentQuality *= 0.9;
                                tryCompress();
                            }
                        },
                        file.type,
                        currentQuality
                    );
                };

                tryCompress();
            };

            img.onerror = () => {
                reject(new Error('Failed to load image'));
            };
        };

        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };
    });
};

/**
 * Validate if file is an image
 * @param file - File to validate
 * @returns boolean
 */
export const isValidImageFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    return validTypes.includes(file.type);
};

/**
 * Format file size to human readable format
 * @param bytes - File size in bytes
 * @returns Formatted string
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};